import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  RELACION_VENTAS_TITULO_DEFAULT,
  applyRelacionVentasColumnLabelsFromApi,
  buildRelacionFootCells,
  cajaGroupBanner,
  cajaGroupKey,
  turnoGroupBanner,
  turnoGroupKey,
  extractRelacionApiFootTotals,
  formatRelacionCell,
  mergeRelacionTotalsSlice,
  mergeRelacionVentasEncabezado,
  resolveRelacionVentasColumns,
  rowToRelacionNorm,
  sortRelacionRowsForDisplay,
  sumRelacionTotals,
  withRelacionVentasPeriodoLine,
} from './relacionVentasReportModel';
import {
  columnWidthsProportionalMm,
  drawEncabezadoReportePdf,
  pdfTableInnerWidthMm,
} from './reporteEncabezadoPdf';

const TABLE_HEAD_BG = [15, 47, 89];
const TABLE_HEAD_TEXT = [255, 255, 255];

const cellPdf = (spec, value) => {
  const s = formatRelacionCell(spec, value);
  return s === '—' ? '—' : s;
};

/**
 * @param {object[]} rawRows
 * @param {{
 *   fechaInicio?: string,
 *   fechaFin?: string,
 *   cajaInicio?: number,
 *   cajaFin?: number,
 *   turnoInicio?: number,
 *   turnoFin?: number,
 *   fileBase?: string,
 *   encabezado?: object|null,
 *   encabezadoFallback?: object,
 *   reporteTitulo?: string,
 *   apiTotales?: object|null,
 *   columnas?: Record<string, string>|null,
 *   ordenarMesa?: boolean,
 *   agruparTurno?: boolean,
 * }} options
 */
export function downloadRelacionVentasPdf(rawRows, options = {}) {
  if (!Array.isArray(rawRows)) return;

  const {
    fechaInicio,
    fechaFin,
    cajaInicio,
    cajaFin,
    turnoInicio,
    turnoFin,
    fileBase = 'relacion-ventas',
    encabezado = null,
    encabezadoFallback = null,
    sucursalNombre = '',
    reporteTitulo = RELACION_VENTAS_TITULO_DEFAULT,
    apiTotales = null,
    columnas = null,
    ordenarMesa = false,
    agruparTurno = false,
  } = options;

  const resolvedBase = resolveRelacionVentasColumns(rawRows[0] ?? null);
  const resolved = applyRelacionVentasColumnLabelsFromApi(resolvedBase, columnas);
  const nCol = resolved.length;

  const sorted = sortRelacionRowsForDisplay(rawRows, resolved, ordenarMesa);
  const normRows = sorted.map((r) => rowToRelacionNorm(r, resolved));
  const summed = sumRelacionTotals(normRows, resolved);
  const { venta: apiVenta, cortesia: apiCortesia } = extractRelacionApiFootTotals(apiTotales);
  const totalsVenta = mergeRelacionTotalsSlice(summed, apiVenta, resolved);
  const zeroTotals = Object.fromEntries(
    resolved.filter((c) => c.total).map((c) => [c.id, 0])
  );
  const totalsCortesia = mergeRelacionTotalsSlice(zeroTotals, apiCortesia, resolved);

  const encMerged = withRelacionVentasPeriodoLine(
    {
      ...mergeRelacionVentasEncabezado(encabezado, encabezadoFallback),
      reporteTitulo,
    },
    fechaInicio,
    fechaFin
  );

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 10;

  // Sucursal encima del encabezado (igual que popularidad)
  let y = margin;
  const sn = String(sucursalNombre || '').trim();
  const ncUp = String(encMerged.nombreComercial || '').toUpperCase();
  if (sn && !ncUp.includes(sn.toUpperCase())) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(15, 23, 42);
    doc.text(sn.toUpperCase(), pageW / 2, y, { align: 'center' });
    y += 5;
    doc.setTextColor(0);
  }

  y = drawEncabezadoReportePdf(
    doc,
    encMerged,
    pageW,
    margin,
    y,
    {
      fechaInicio,
      fechaFin,
      cajaInicio,
      cajaFin,
      turnoInicio,
      turnoFin,
    },
    {
      omitResponsable: true,
      omitDomicilio: false,
      tituloResaltadoRgb: [191, 219, 254],
    }
  );

  const head = [resolved.map((c) => c.label)];

  /** @type {(string | { content: string, colSpan?: number, styles?: object })[][]} */
  const body = [];
  let lastTurnKey;
  let lastGroupKey;
  for (const raw of sorted) {
    if (agruparTurno) {
      const tk = turnoGroupKey(raw);
      if (tk && tk !== lastTurnKey) {
        lastTurnKey = tk;
        lastGroupKey = undefined;
        body.push([
          {
            content: String(turnoGroupBanner(raw)),
            colSpan: nCol,
            styles: {
              fontStyle: 'bold',
              fillColor: [15, 47, 89],
              textColor: [224, 242, 254],
              halign: 'left',
            },
          },
        ]);
      }
      const gk = cajaGroupKey(raw);
      if (gk !== lastGroupKey) {
        lastGroupKey = gk;
        const banner = cajaGroupBanner(raw);
        body.push([
          {
            content: String(banner).toUpperCase(),
            colSpan: nCol,
            styles: {
              fontStyle: 'bold',
              fillColor: [241, 245, 249],
              textColor: [15, 23, 42],
              halign: 'left',
            },
          },
        ]);
      }
    }
    const norm = rowToRelacionNorm(raw, resolved);
    body.push(resolved.map((c) => cellPdf(c, norm[c.id])));
  }

  const foot = [
    buildRelacionFootCells(resolved, 'TOTAL VENTA', totalsVenta),
    buildRelacionFootCells(resolved, 'TOTAL CORTESIA', totalsCortesia),
  ];

  const colW = [16, 11, 11, 14, 20, 20, 17, 18, 20, 18, 11, 36, 13, 13];
  const innerW = pdfTableInnerWidthMm(pageW, margin);
  const weights = resolved.map((_, i) => colW[i] ?? 14);
  const widths = columnWidthsProportionalMm(weights, innerW);

  autoTable(doc, {
    startY: y,
    tableWidth: innerW,
    head,
    body,
    foot,
    showFoot: 'lastPage',
    theme: 'plain',
    tableLineColor: [0, 0, 0],
    headStyles: {
      fillColor: TABLE_HEAD_BG,
      textColor: TABLE_HEAD_TEXT,
      fontStyle: 'bold',
      lineWidth: { top: 0.2, bottom: 0.28, left: 0.06, right: 0.06 },
      lineColor: [30, 58, 110],
    },
    footStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontStyle: 'bold',
      lineWidth: { top: 0, bottom: 0.1, left: 0, right: 0 },
    },
    bodyStyles: {
      textColor: [0, 0, 0],
      lineWidth: { top: 0, bottom: 0, left: 0, right: 0 },
    },
    styles: { fontSize: 6.5, cellPadding: 1 },
    columnStyles: Object.fromEntries(
      resolved.map((c, i) => [i, { halign: c.align, cellWidth: widths[i] }])
    ),
    margin: { left: margin, right: margin },
    didParseCell: (data) => {
      const colIdx = data.column.index;
      if (data.section === 'head' && colIdx < resolved.length) {
        const c = resolved[colIdx];
        data.cell.styles.halign = c.align;
        data.cell.styles.fillColor = TABLE_HEAD_BG;
        data.cell.styles.textColor = TABLE_HEAD_TEXT;
        data.cell.styles.fontStyle = 'bold';
      }
      if (data.section === 'foot' && colIdx < resolved.length) {
        const c = resolved[colIdx];
        data.cell.styles.halign = c.align;
        if (c.total || colIdx === 0) {
          data.cell.styles.lineWidth = { top: 0.4, bottom: 0.15, left: 0, right: 0 };
        } else {
          data.cell.styles.lineWidth = { top: 0, bottom: 0.15, left: 0, right: 0 };
        }
      }
    },
  });

  const totalPages = doc.internal.getNumberOfPages();
  const ph = doc.internal.pageSize.getHeight();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(7.5);
    doc.setTextColor(90);
    doc.text(`Generado ${new Date().toLocaleString('es-MX')}`, margin, ph - 7);
    doc.text(`Página ${i} de ${totalPages}`, pageW - margin, ph - 7, { align: 'right' });
    doc.setTextColor(0);
  }

  doc.save(`${fileBase}-${fechaInicio || 'inicio'}-${fechaFin || 'fin'}.pdf`);
}
